import { useCallback, useEffect, useState } from "react";
import {
  approvePrivacyRequest,
  assignPrivacyRequest,
  fetchPrivacyDashboard,
  listPrivacyIncidents,
  listPrivacyRequests,
  listPrivacyVendors,
  listRetentionRules,
  rejectPrivacyRequest,
} from "../../../../api/adminPrivacyApi";
import RequirePermission from "../../../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import StatusBadge from "../../intelligence/StatusBadge";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";

function maskId(value) {
  if (!value) return "—";
  const s = String(value);
  return s.length <= 8 ? s : `${s.slice(0, 8)}…`;
}

function statusTone(status) {
  if (!status) return "neutral";
  if (status === "completed") return "confirmed";
  if (status.includes("blocked") || status === "rejected" || status === "failed") {
    return "severe";
  }
  if (status === "scheduled" || status === "processing") return "medium";
  return "neutral";
}

function AdminPrivacyOperationsPageContent({ onClose }) {
  const [tab, setTab] = useState("queue");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [rules, setRules] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, req, vend, ret, inc] = await Promise.all([
        fetchPrivacyDashboard().catch(() => null),
        listPrivacyRequests({
          status: statusFilter || undefined,
          limit: 100,
        }),
        listPrivacyVendors().catch(() => ({ vendors: [] })),
        listRetentionRules().catch(() => ({ rules: [] })),
        listPrivacyIncidents({ limit: 50 }).catch(() => ({ incidents: [] })),
      ]);
      setDashboard(dash?.dashboard || dash || null);
      setRequests(req?.requests || []);
      setVendors(vend?.vendors || []);
      setRules(ret?.rules || []);
      setIncidents(inc?.incidents || []);
    } catch (err) {
      setError(err.message || "Failed to load privacy operations.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAssign(id) {
    setBusyId(id);
    try {
      await assignPrivacyRequest(id, {});
      await load();
    } catch (err) {
      setError(err.message || "Assign failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleApprove(id) {
    if (!window.confirm("Approve and execute this privacy request?")) return;
    setBusyId(id);
    try {
      await approvePrivacyRequest(id, {});
      await load();
    } catch (err) {
      setError(err.message || "Approve failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(id) {
    const reason = window.prompt("Rejection reason (visible to operators, not full legal analysis):");
    if (reason == null) return;
    setBusyId(id);
    try {
      await rejectPrivacyRequest(id, { reason, reasonCode: "admin_rejected" });
      await load();
    } catch (err) {
      setError(err.message || "Reject failed.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-page__header">
        <div>
          <p className="eyebrow">Privacy operations</p>
          <h1>AutoMapa privacy cases</h1>
          <p className="muted">
            Request queue, vendors, retention rules, and incidents. List views use masked
            identifiers.
          </p>
        </div>
        <div className="admin-page__header-actions">
          <button type="button" className="midpoint-share-button" onClick={load}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="midpoint-share-button" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {dashboard ? (
        <section className="admin-dash__metrics" aria-label="Privacy dashboard metrics">
          <div className="admin-dash__metric">
            <span className="muted">Open</span>
            <strong>{dashboard.openCount ?? dashboard.openRequests ?? "—"}</strong>
          </div>
          <div className="admin-dash__metric">
            <span className="muted">Nearing due</span>
            <strong>{dashboard.nearingDue ?? "—"}</strong>
          </div>
          <div className="admin-dash__metric">
            <span className="muted">Identity pending</span>
            <strong>{dashboard.identityPending ?? "—"}</strong>
          </div>
          <div className="admin-dash__metric">
            <span className="muted">Deletion in progress</span>
            <strong>{dashboard.deletionInProgress ?? "—"}</strong>
          </div>
        </section>
      ) : null}

      <div className="admin-tabs" role="tablist" aria-label="Privacy sections">
        {[
          ["queue", "Request queue"],
          ["vendors", "Vendors"],
          ["retention", "Retention rules"],
          ["incidents", "Incidents"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={tab === id ? "admin-tabs__tab is-active" : "admin-tabs__tab"}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {error ? <AdminStatePanel tone="error" title="Error" message={error} /> : null}
      {loading ? <AdminStatePanel title="Loading" message="Loading privacy operations…" /> : null}

      {!loading && tab === "queue" ? (
        <AdminTableContainer>
          <div className="admin-toolbar">
            <label htmlFor="privacy-status-filter">
              Status
              <select
                id="privacy-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All open-ish</option>
                <option value="submitted">submitted</option>
                <option value="in_review">in_review</option>
                <option value="scheduled">scheduled</option>
                <option value="processing">processing</option>
                <option value="completed">completed</option>
                <option value="blocked_by_legal_hold">blocked_by_legal_hold</option>
              </select>
            </label>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Reference</th>
                <th scope="col">Type</th>
                <th scope="col">Status</th>
                <th scope="col">Due</th>
                <th scope="col">Subject</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="muted">
                    No privacy requests.
                  </td>
                </tr>
              ) : (
                requests.map((row) => (
                  <tr key={row.requestId || row.id || row.requestUuid}>
                    <td>
                      <code>{maskId(row.requestUuid || row.requestId || row.id)}</code>
                    </td>
                    <td>{row.requestType}</td>
                    <td>
                      <StatusBadge label={row.status || row.requestStatus} tone={statusTone(row.status || row.requestStatus)} />
                    </td>
                    <td>{row.dueAt ? String(row.dueAt).slice(0, 16) : "—"}</td>
                    <td>{maskId(row.publicUserRef || row.publicUserId)}</td>
                    <td>
                      <div className="admin-table__actions">
                        <button
                          type="button"
                          disabled={busyId === (row.requestId || row.id)}
                          onClick={() => handleAssign(row.requestId || row.id)}
                        >
                          Assign
                        </button>
                        <button
                          type="button"
                          disabled={busyId === (row.requestId || row.id)}
                          onClick={() => handleApprove(row.requestId || row.id)}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={busyId === (row.requestId || row.id)}
                          onClick={() => handleReject(row.requestId || row.id)}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {!loading && tab === "vendors" ? (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Vendor</th>
                <th scope="col">Category</th>
                <th scope="col">DPA</th>
                <th scope="col">Security</th>
                <th scope="col">Active</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id || v.vendor_key}>
                  <td>{v.vendor_name || v.vendorName || v.vendor_key}</td>
                  <td>{v.service_category || v.serviceCategory || "—"}</td>
                  <td>{v.data_processing_agreement_status || v.dpaStatus || "—"}</td>
                  <td>{v.security_review_status || "—"}</td>
                  <td>{v.active ? "yes" : "no"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {!loading && tab === "retention" ? (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">System</th>
                <th scope="col">Action</th>
                <th scope="col">Days</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id || `${r.data_category}-${r.system_key}`}>
                  <td>{r.data_category || r.dataCategory}</td>
                  <td>{r.system_key || r.systemKey}</td>
                  <td>{r.deletion_action || r.deletionAction}</td>
                  <td>{r.retention_period_days ?? r.retentionPeriodDays ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {!loading && tab === "incidents" ? (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">UUID</th>
                <th scope="col">Type</th>
                <th scope="col">Severity</th>
                <th scope="col">Status</th>
                <th scope="col">Detected</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((inc) => (
                <tr key={inc.id || inc.incident_uuid}>
                  <td>
                    <code>{maskId(inc.incident_uuid || inc.incidentUuid)}</code>
                  </td>
                  <td>{inc.incident_type || inc.incidentType}</td>
                  <td>{inc.severity}</td>
                  <td>{inc.status}</td>
                  <td>
                    {inc.detected_at || inc.detectedAt
                      ? String(inc.detected_at || inc.detectedAt).slice(0, 16)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}
    </div>
  );
}

export default function AdminPrivacyOperationsPage(props) {
  return (
    <RequirePermission
      permissions={[
        ADMIN_PERMISSIONS.PRIVACY_REQUESTS_VIEW,
        ADMIN_PERMISSIONS.PRIVACY_REQUESTS_MANAGE,
        ADMIN_PERMISSIONS.SYSTEM_AUDIT_VIEW,
      ]}
      mode="any"
    >
      <AdminPrivacyOperationsPageContent {...props} />
    </RequirePermission>
  );
}
